'use client';

import { useState, useCallback } from 'react';
import { PageHeader } from '@/shared/layout';
import { Select } from '@/shared/ui';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle2, Shield } from 'lucide-react';
import { usePageContent } from '@/core/i18n/content';
import { CONTACT_CONTENT } from '@/core/i18n/content/contact.content';

/* -------------------------------------------------------------------------- */
/*  reCAPTCHA v3 helper                                                       */
/* -------------------------------------------------------------------------- */

const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '';

function loadRecaptchaScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return reject(new Error('SSR'));
    if ((window as any).grecaptcha) { resolve(); return; }
    const existing = document.getElementById('recaptcha-v3-script');
    if (existing) { resolve(); return; }
    const script = document.createElement('script');
    script.id = 'recaptcha-v3-script';
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('reCAPTCHA script failed to load'));
    document.head.appendChild(script);
  });
}

async function getRecaptchaToken(action: string): Promise<string> {
  if (!RECAPTCHA_SITE_KEY) return 'recaptcha-not-configured';
  try {
    await loadRecaptchaScript();
    const grecaptcha = (window as any).grecaptcha;
    if (!grecaptcha) return 'recaptcha-unavailable';
    return await new Promise<string>((resolve) => {
      grecaptcha.ready(() => {
        grecaptcha.execute(RECAPTCHA_SITE_KEY, { action }).then(resolve);
      });
    });
  } catch {
    return 'recaptcha-error';
  }
}

const CONTACT_ICONS = [MapPin, Phone, Mail, Clock];

/* -------------------------------------------------------------------------- */
/*  ContactPage                                                               */
/* -------------------------------------------------------------------------- */

export function ContactPage() {
  const c = usePageContent(CONTACT_CONTENT);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const [error, setError] = useState('');

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const recaptchaToken = await getRecaptchaToken('contact_submit');
      const res = await fetch('/api/v1/public/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, recaptchaToken }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(err.error ?? 'Failed to send message');
      }
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [submitting, form]);

  const inputCls = 'w-full rounded-lg border border-hms-border bg-hms-surface px-3.5 py-2.5 text-sm text-hms-text placeholder:text-hms-textDim focus:border-hms-gold focus:ring-1 focus:ring-hms-gold/30 outline-none transition-colors';

  return (
    <div id="contact-page" className="min-h-screen bg-hms-bg">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <PageHeader title={c.header.title} description={c.header.description} />

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Contact form */}
          <div className="lg:col-span-3 rounded-xl border border-hms-border bg-hms-card p-6">
            {submitted ? (
              <div id="contact-success" className="flex flex-col items-center justify-center py-16 text-center">
                <CheckCircle2 className="h-12 w-12 text-hms-green mb-4" />
                <h3 className="text-lg font-semibold text-hms-text">{c.success.title}</h3>
                <p className="mt-2 text-sm text-hms-textSub max-w-sm">{c.success.message}</p>
                <button id="contact-send-another" type="button"
                  onClick={() => { setSubmitted(false); setForm({ name: '', email: '', subject: '', message: '' }); }}
                  className="mt-6 text-sm font-medium text-hms-gold hover:text-hms-goldLt transition-colors">
                  {c.success.sendAnother}
                </button>
              </div>
            ) : (
              <form id="contact-form" onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div id="contact-error" className="rounded-lg border border-hms-red/30 bg-hms-red/5 px-4 py-3 text-sm text-hms-red">
                    {error}
                  </div>
                )}
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-hms-text mb-1.5">{c.form.name}</label>
                  <input id="contact-name" name="name" required value={form.name} onChange={handleChange}
                    className={inputCls} placeholder={c.form.namePlaceholder} />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-hms-text mb-1.5">{c.form.email}</label>
                  <input id="contact-email" name="email" type="email" required value={form.email} onChange={handleChange}
                    className={inputCls} placeholder={c.form.emailPlaceholder} />
                </div>
                <Select id="contact-subject" label={c.form.subject} placeholder={c.form.subjectPlaceholder} options={c.subjects.map((opt) => ({ label: opt, value: opt }))} value={form.subject} onChange={handleChange} />
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-hms-text mb-1.5">{c.form.message}</label>
                  <textarea id="contact-message" name="message" rows={5} required value={form.message} onChange={handleChange}
                    className={`${inputCls} resize-none`} placeholder={c.form.messagePlaceholder} />
                </div>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <button id="contact-submit" type="submit" disabled={submitting}
                    className="inline-flex items-center gap-2 rounded-lg bg-hms-gold px-5 py-2.5 text-sm font-semibold text-white hover:bg-hms-gold/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                    <Send className="h-4 w-4" /> {submitting ? c.form.sending : c.form.submit}
                  </button>
                  <div id="contact-recaptcha-badge" className="flex items-center gap-1.5 text-[10px] text-hms-textDim">
                    <Shield className="h-3 w-3" />
                    <span>{c.recaptcha} &middot; <a id="legal-contact-recaptcha-privacy" href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-hms-textSub">{c.recaptchaPrivacy}</a> &middot; <a id="legal-contact-recaptcha-terms" href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="hover:text-hms-textSub">{c.recaptchaTerms}</a></span>
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* Contact info sidebar */}
          <div className="lg:col-span-2 space-y-4">
            {c.contactInfo.map((item, idx) => {
              const Icon = CONTACT_ICONS[idx] ?? MapPin;
              return (
                <div key={idx} id={`contact-info-${idx}`} className="rounded-xl border border-hms-border bg-hms-card p-5 space-y-2">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-hms-gold" />
                    <h3 className="text-sm font-semibold text-hms-text">{item.label}</h3>
                  </div>
                  <p className="text-sm text-hms-textSub leading-relaxed">{item.value}</p>
                </div>
              );
            })}
            <div className="rounded-xl border border-hms-border bg-hms-card p-5 space-y-3">
              <h3 className="text-sm font-semibold text-hms-text">{c.officesTitle}</h3>
              <div className="space-y-3">
                {c.offices.map((office, idx) => (
                  <div key={idx} id={`contact-office-${['americas', 'europe', 'middle-east', 'asia-pacific'][idx] ?? idx}`} className="border-t border-hms-border/50 pt-3 first:border-0 first:pt-0">
                    <p className="text-sm font-medium text-hms-gold">{office.region}</p>
                    <p className="text-xs text-hms-textSub mt-0.5">{office.location}</p>
                    <p className="text-xs text-hms-textDim">{office.hours} · {office.languages}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
